import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangePicker, Spinner } from '@crazyegginc/hatch';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';

import { DateRange } from '/src/components/date-time/DateRange';
import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SitePickerBar } from '/src/components/site-picker';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { usePermissions, useQueryParams, useSite, useTimeZonedFunctions } from '/src/hooks';
import { FEATURES, TIMEZONES } from '/src/features/_global/constants';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import { DEFAULT_LIMIT, DEFAULT_PRESET_DATE, formatPlatform } from '../constants';
import { adPlatformAnalyticsQuery } from '../queries';
import { SummaryCards } from '../components/SummaryCards';
import { SpendOverTimeChart } from '../components/SpendOverTimeChart';
import { PlatformBreakdown } from '../components/PlatformBreakdown';
import { TopCampaignsTable } from '../components/TopCampaignsTable';
import { TopAdsTable } from '../components/TopAdsTable';
import { PlatformPillFilter } from '../components/PlatformPillFilter';
import { NoConnectionsWall } from '../walls/NoConnections';

const DATE_RANGE_PARAM = 'date_range';
const AD_PERFORMANCE_MAX_ALLOWABLE_RANGE = {
  unit: 'days',
  value: 365,
  maxRange: 365,
};
const PLATFORM_ORDER = {
  META: 0,
  GOOGLE: 1,
  REDDIT: 2,
};

export function AdPerformanceDashboard() {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.AD_PLATFORM_ANALYTICS);
  const { sites, selectedSite, selectSite, loadingSites } = useSite();
  const { get: getParam, set: setParam } = useQueryParams();
  const { getDefaultDateRange, resolveDateRangeParam } = useTimeZonedFunctions(TIMEZONES.UTC);
  const location = useLocation();
  const dateRangeParam = getParam(DATE_RANGE_PARAM);
  const [featureStartedAt, setFeatureStartedAt] = useState(undefined);

  useEffect(() => {
    setFeatureStartedAt(undefined);
  }, [selectedSite?.id]);

  const defaultDateRange = useMemo(
    () =>
      getDefaultDateRange({
        featureStartedAt,
      }),
    [featureStartedAt, getDefaultDateRange],
  );

  const resolvedDateRange = useMemo(
    () =>
      resolveDateRangeParam({
        rawValue: dateRangeParam,
        adapter: JSON_DATE_RANGE_ADAPTER,
        defaultDateRange,
        featureStartedAt,
        maxAllowableRange: AD_PERFORMANCE_MAX_ALLOWABLE_RANGE,
      }),
    [dateRangeParam, defaultDateRange, featureStartedAt, resolveDateRangeParam],
  );

  useEffect(() => {
    if (!resolvedDateRange?.serializedValue) {
      return;
    }

    if (!dateRangeParam) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
      return;
    }

    if (resolvedDateRange.shouldRewrite) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
    }
  }, [dateRangeParam, resolvedDateRange?.serializedValue, resolvedDateRange?.shouldRewrite, setParam]);

  const dateRangeConfig = resolvedDateRange?.selectedDateRange ?? defaultDateRange;

  const chooseDateRange = useCallback(
    (value) => {
      const serialized = JSON_DATE_RANGE_ADAPTER.serialize(value);

      if (serialized) {
        setParam(DATE_RANGE_PARAM, serialized);
      }
    },
    [setParam],
  );

  const queryDateArgs = useMemo(() => {
    if (!dateRangeConfig) {
      return { presetDate: DEFAULT_PRESET_DATE };
    }

    if (dateRangeConfig.special) {
      return { presetDate: dateRangeConfig.special };
    }

    return {
      startAt: resolvedDateRange?.convertedDateRange?.startDate,
      endAt: resolvedDateRange?.convertedDateRange?.endDate,
    };
  }, [
    dateRangeConfig,
    resolvedDateRange?.convertedDateRange?.endDate,
    resolvedDateRange?.convertedDateRange?.startDate,
  ]);

  const { data, isLoading, isFetching } = useQuery({
    ...adPlatformAnalyticsQuery({
      siteId: selectedSite?.id,
      limit: DEFAULT_LIMIT,
      ...queryDateArgs,
    }),
  });

  const dashboard = data?.adPlatformAnalytics;
  const nextFeatureStartedAt = useMemo(
    () => getEarliestOldestImportedDataAt(dashboard?.connections),
    [dashboard?.connections],
  );

  useEffect(() => {
    setFeatureStartedAt((current) => (current === nextFeatureStartedAt ? current : nextFeatureStartedAt));
  }, [nextFeatureStartedAt]);

  const hasConnections = (dashboard?.connections ?? []).length > 0;
  const loading = loadingSites || (selectedSite && (isLoading || isFetching));

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.AD_PLATFORM_ANALYTICS} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title="Ad Performance" />
      <DashHeader title="Ad Performance" />
      <PaymentAlerts />
      <SitePickerBar
        sites={sites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        loading={loadingSites}
        addSiteOption={permissions.can('create', FEATURES.SITES).allowed}
        pageDisplayName="Ad Performance dashboard"
      />
      <div className="flex flex-col gap-6 p-10 !pt-5">
        {loading ? (
          <div className="mt-16 flex w-full items-center justify-center">
            <Spinner />
            <div className="ml-2.5">Loading...</div>
          </div>
        ) : !selectedSite ? null : !hasConnections ? (
          <NoConnectionsWall />
        ) : (
          <>
            <section className="mb-1 flex items-center justify-between gap-4">
              <div className="ml-1 text-lg font-semibold text-black-pearl-500">
                <DateRange dateRange={dateRangeConfig} timeZone={TIMEZONES.UTC} />
              </div>
              <DateRangePicker
                lowerBoundary={resolvedDateRange?.pickerProps.lowerBoundary}
                upperBoundary={resolvedDateRange?.pickerProps.upperBoundary}
                startDate={resolvedDateRange?.pickerProps.startDate}
                endDate={resolvedDateRange?.pickerProps.endDate}
                special={resolvedDateRange?.pickerProps.special}
                setDateRange={chooseDateRange}
                setCommonDateRange={chooseDateRange}
                showCommonRanges={true}
                size="lg"
                maxRange={resolvedDateRange?.pickerProps.maxRange}
                timezone={TIMEZONES.UTC}
                showTimeZone={true}
              />
            </section>
            <SummaryCardsPanel siteId={selectedSite.id} dashboard={dashboard} queryDateArgs={queryDateArgs} />
            <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <SpendOverTimeChart daily={dashboard.daily} />
              <ConnectionPanel connections={dashboard.connections} />
            </section>
            <PlatformBreakdown platformTotals={dashboard.platformTotals} />
            <TopCampaignsPanel siteId={selectedSite.id} dashboard={dashboard} queryDateArgs={queryDateArgs} />
            <TopAdsPanel
              siteId={selectedSite.id}
              dashboard={dashboard}
              queryDateArgs={queryDateArgs}
              dashboardSearch={location.search}
            />
          </>
        )}
      </div>
    </DashboardPage>
  );
}

function ConnectionPanel({ connections = [] }) {
  return (
    <section className="rounded-2xl border border-mystic-500 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Connections</p>
      <h2 className="mt-2 text-lg font-semibold text-black-pearl-500">Sync status</h2>
      <div className="mt-6 space-y-4">
        {connections.map((connection) => (
          <article key={connection.connectionId} className="rounded-xl border border-solitude-500 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-black-pearl-500">{formatPlatform(connection.platform)}</p>
                <p className="mt-1 text-xs text-lynch-500">Account {connection.accountId}</p>
              </div>
              <div className="rounded-full bg-solitude-500 px-3 py-1 text-xs font-semibold text-[#00509D]">
                {connection.lastSyncedDate || 'Pending'}
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-6 rounded-xl bg-solitude-500 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Connected accounts</p>
        <p className="mt-2 text-xl font-semibold text-black-pearl-500">{connections.length}</p>
        <p className="mt-2 text-xs text-lynch-500">
          Each enabled connection is checked by the shared hourly ad-sync worker.
        </p>
      </div>
    </section>
  );
}

function SummaryCardsPanel({ siteId, dashboard, queryDateArgs }) {
  const availablePlatforms = useMemo(() => getAvailablePlatforms(dashboard?.connections), [dashboard?.connections]);
  const [selectedPlatform, setSelectedPlatform] = usePlatformFilter(siteId, availablePlatforms);

  return (
    <section className="space-y-4" aria-label="Ad performance summary section">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Summary View</p>
          <div className="mt-3">
            <PlatformPillFilter
              ariaLabel="Filter summary cards by platform"
              platforms={availablePlatforms}
              selectedPlatform={selectedPlatform}
              onSelectPlatform={setSelectedPlatform}
            />
          </div>
        </div>
        <p className="text-sm text-lynch-500">
          {selectedPlatform
            ? `${formatPlatform(selectedPlatform)} summary cards`
            : 'Blended totals across all ad providers'}
        </p>
      </div>
      {selectedPlatform ? (
        <FilteredSummaryCards siteId={siteId} platform={selectedPlatform} queryDateArgs={queryDateArgs} />
      ) : (
        <SummaryCards summary={dashboard?.summary} daily={dashboard?.daily} />
      )}
    </section>
  );
}

function FilteredSummaryCards({ siteId, platform, queryDateArgs }) {
  const filteredDashboard = useFilteredDashboard(siteId, platform, queryDateArgs);
  const platformLabel = formatPlatform(platform);

  if (!filteredDashboard.hasData && (filteredDashboard.isLoading || filteredDashboard.isFetching)) {
    return (
      <FilteredStateCard ariaLabel="Ad performance summary" message={`Loading ${platformLabel} summary data...`} />
    );
  }

  if (!filteredDashboard.hasData && filteredDashboard.error) {
    return (
      <FilteredStateCard
        ariaLabel="Ad performance summary"
        message={`We couldn't load ${platformLabel} summary data right now.`}
        tone="error"
      />
    );
  }

  if (!filteredDashboard.summary) {
    return (
      <FilteredStateCard
        ariaLabel="Ad performance summary"
        message={`No ${platformLabel} summary data is available for the selected range yet.`}
      />
    );
  }

  return <SummaryCards summary={filteredDashboard.summary} daily={filteredDashboard.daily} />;
}

function TopCampaignsPanel({ siteId, dashboard, queryDateArgs }) {
  const availablePlatforms = useMemo(() => getAvailablePlatforms(dashboard?.connections), [dashboard?.connections]);
  const [selectedPlatform, setSelectedPlatform] = usePlatformFilter(siteId, availablePlatforms);

  return selectedPlatform ? (
    <FilteredTopCampaignsTable
      siteId={siteId}
      platform={selectedPlatform}
      queryDateArgs={queryDateArgs}
      platforms={availablePlatforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={setSelectedPlatform}
    />
  ) : (
    <TopCampaignsTable
      campaigns={dashboard?.topCampaigns}
      platforms={availablePlatforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={setSelectedPlatform}
    />
  );
}

function FilteredTopCampaignsTable({ siteId, platform, queryDateArgs, platforms, selectedPlatform, onSelectPlatform }) {
  const filteredDashboard = useFilteredDashboard(siteId, platform, queryDateArgs);

  return (
    <TopCampaignsTable
      campaigns={filteredDashboard.topCampaigns}
      platforms={platforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={onSelectPlatform}
      isLoading={!filteredDashboard.hasData && (filteredDashboard.isLoading || filteredDashboard.isFetching)}
      error={!filteredDashboard.hasData ? filteredDashboard.error : null}
    />
  );
}

function TopAdsPanel({ siteId, dashboard, queryDateArgs, dashboardSearch = '' }) {
  const availablePlatforms = useMemo(() => getAvailablePlatforms(dashboard?.connections), [dashboard?.connections]);
  const [selectedPlatform, setSelectedPlatform] = usePlatformFilter(siteId, availablePlatforms);
  const getAdHref = useCallback((ad) => `/ad-performance/ads/${ad.id}${dashboardSearch}`, [dashboardSearch]);

  return selectedPlatform ? (
    <FilteredTopAdsTable
      siteId={siteId}
      platform={selectedPlatform}
      queryDateArgs={queryDateArgs}
      platforms={availablePlatforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={setSelectedPlatform}
      getAdHref={getAdHref}
    />
  ) : (
    <TopAdsTable
      ads={dashboard?.topAds}
      platforms={availablePlatforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={setSelectedPlatform}
      getAdHref={getAdHref}
    />
  );
}

function FilteredTopAdsTable({
  siteId,
  platform,
  queryDateArgs,
  platforms,
  selectedPlatform,
  onSelectPlatform,
  getAdHref,
}) {
  const filteredDashboard = useFilteredDashboard(siteId, platform, queryDateArgs);

  return (
    <TopAdsTable
      ads={filteredDashboard.topAds}
      platforms={platforms}
      selectedPlatform={selectedPlatform}
      onSelectPlatform={onSelectPlatform}
      isLoading={!filteredDashboard.hasData && (filteredDashboard.isLoading || filteredDashboard.isFetching)}
      error={!filteredDashboard.hasData ? filteredDashboard.error : null}
      getAdHref={getAdHref}
    />
  );
}

function useFilteredDashboard(siteId, platform, queryDateArgs) {
  const requestKey = [
    siteId,
    platform,
    queryDateArgs?.startAt ?? '',
    queryDateArgs?.endAt ?? '',
    queryDateArgs?.presetDate ?? '',
  ].join(':');
  const { data, isLoading, isFetching, error } = useQuery({
    ...adPlatformAnalyticsQuery({
      siteId,
      limit: DEFAULT_LIMIT,
      platforms: [platform],
      ...queryDateArgs,
    }),
  });
  const filteredDashboard = data?.adPlatformAnalytics ?? null;
  const [lastMatchingFiltered, setLastMatchingFiltered] = useState(null);
  const resolvedDashboard =
    filteredDashboard ?? (lastMatchingFiltered?.requestKey === requestKey ? lastMatchingFiltered.data : null);

  useEffect(() => {
    setLastMatchingFiltered(null);
  }, [siteId, platform, queryDateArgs?.startAt, queryDateArgs?.endAt, queryDateArgs?.presetDate]);

  useEffect(() => {
    if (filteredDashboard && !isEqual(lastMatchingFiltered?.data, filteredDashboard)) {
      setLastMatchingFiltered({ requestKey, data: filteredDashboard });
    }
  }, [filteredDashboard, lastMatchingFiltered, requestKey]);

  return {
    summary: resolvedDashboard?.summary,
    daily: resolvedDashboard?.daily,
    topCampaigns: resolvedDashboard?.topCampaigns,
    topAds: resolvedDashboard?.topAds,
    isLoading,
    isFetching,
    error,
    hasData: Boolean(resolvedDashboard),
  };
}

function FilteredStateCard({ ariaLabel, message, tone = 'default' }) {
  return (
    <div
      aria-label={ariaLabel}
      className={`rounded-2xl border bg-white px-5 py-6 text-sm shadow-sm ${
        tone === 'error' ? 'border-red-200 text-red-700' : 'border-mystic-500 text-lynch-500'
      }`}
    >
      {message}
    </div>
  );
}

function usePlatformFilter(siteId, availablePlatforms) {
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  useEffect(() => {
    setSelectedPlatform(null);
  }, [siteId]);

  useEffect(() => {
    if (selectedPlatform && !availablePlatforms.includes(selectedPlatform)) {
      setSelectedPlatform(null);
    }
  }, [availablePlatforms, selectedPlatform]);

  return [selectedPlatform, setSelectedPlatform];
}

function getAvailablePlatforms(connections = []) {
  return [...new Set(connections.map((connection) => connection?.platform).filter(Boolean))].sort(
    (left, right) =>
      (PLATFORM_ORDER[left] ?? Number.MAX_SAFE_INTEGER) - (PLATFORM_ORDER[right] ?? Number.MAX_SAFE_INTEGER),
  );
}

function getEarliestOldestImportedDataAt(connections = []) {
  const oldestImportedDataAts = connections
    .map((connection) => connection?.oldestImportedDataAt)
    .filter(Number.isFinite);

  if (oldestImportedDataAts.length === 0) {
    return undefined;
  }

  return Math.min(...oldestImportedDataAts);
}
